// components/DomainPasswordDialog.js

import { BrandButton, TextCancelButton } from '../button';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Box,
  LinearProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff, Security, Warning } from '@mui/icons-material';

const DomainPasswordDialog = ({ 
  open, 
  onClose, 
  onSuccess, 
  domain, 
  email,
  action = 'add domain' // 'add domain' или 'add email'
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [warning, setWarning] = useState('');

  // Сбрасываем состояние при открытии диалога
  useEffect(() => {
    if (open) {
      setPassword('');
      setError('');
      setWarning('');
      setAttempts(0);
      setRemainingAttempts(3);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError('');
    setWarning('');

    try {
      const response = await fetch('/api/domains/verify-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          domain: domain,
          password: password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Пароль правильный
        console.log('✅ Domain password verified successfully');
        onSuccess(password);
        handleClose();
      } else {
        // Пароль неправильный
        setError(data.error || 'Invalid password');
        setAttempts(data.attempts || 0);
        setRemainingAttempts(data.remainingAttempts || 0);
        setWarning(data.warning || '');
        setPassword(''); // Очищаем поле пароля
        
        // Если закончились попытки, закрываем диалог
        if (data.remainingAttempts <= 0) {
          setTimeout(() => {
            handleClose();
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Error verifying domain password:', error);
      setError('Failed to verify password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    setWarning('');
    setAttempts(0);
    setRemainingAttempts(3);
    onClose();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getProgressColor = () => {
    if (remainingAttempts === 3) return 'success';
    if (remainingAttempts === 2) return 'warning';
    return 'error';
  };

  const getProgressValue = () => {
    return (remainingAttempts / 3) * 100;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Security color="warning" />
          <Typography variant="h6">
            Protected Domain Access
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              🔒 Protected Domain: {domain || (email ? email.split('@')[1] : '')}
            </Typography>
            <Typography variant="body2">
              This domain requires special authorization. Only administrators and authorized users 
              can {action === 'add email' ? 'add emails from' : 'add'} this domain.
            </Typography>
          </Alert>

          {attempts > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Remaining attempts: {remainingAttempts} / 3
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={getProgressValue()} 
                color={getProgressColor()}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          )}

          {warning && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <Warning sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                {warning}
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Domain Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading || remainingAttempts <= 0}
              autoFocus
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      disabled={loading}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          </form>

          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            💡 If you believe you should have access to this domain, please contact your administrator.
          </Typography>
        </Box>

        {remainingAttempts <= 0 && (
          <Alert severity="error">
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              🚫 Account Blocked
            </Typography>
            <Typography variant="body2">
              Your account has been blocked from adding domains and emails due to multiple failed attempts. 
              Please contact support for assistance.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <TextCancelButton 
          onClick={handleClose} 
          disabled={loading}
        >
          Cancel
        </TextCancelButton>
        <BrandButton 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !password.trim() || remainingAttempts <= 0}
        >
          {loading ? 'Verifying...' : 'Verify Password'}
        </BrandButton>
      </DialogActions>
    </Dialog>
  );
};

export default DomainPasswordDialog; 